import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Save, AlertCircle } from 'lucide-react';
import { X } from './icons';
import api from '../services/api';

export default function CrearEvidenciaModal({ isOpen, onClose, onEvidenciaCreada }) {
    const [formData, setFormData] = useState({
        codigoControl: '',
        descripcionControl: '',
        prioridad: 'Media',
        intervaloRevision: 'Anual',
        listaVerificacion: '',
        descripcionImplementacion: '',
        tipo: 'Implementación',
        hoja: '',
        comentarios: '',
        requiereEvidencia: true
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { data: nuevaEvidencia } = await api.post('/EvidenciaSeguridad', formData);

            // Limpiar formulario
            setFormData({
                codigoControl: '',
                descripcionControl: '',
                prioridad: 'Media',
                intervaloRevision: 'Anual',
                listaVerificacion: '',
                descripcionImplementacion: '',
                tipo: 'Implementación',
                hoja: '',
                comentarios: '',
                requiereEvidencia: true
            });

            // Notificar al componente padre
            onEvidenciaCreada(nuevaEvidencia);
            onClose();

        } catch (error) {
            console.error('Error al crear evidencia:', error);
            setError(error.response?.data?.message || error.message || 'Error al crear evidencia');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({
                codigoControl: '',
                descripcionControl: '',
                prioridad: 'Media',
                intervaloRevision: 'Anual',
                listaVerificacion: '',
                descripcionImplementacion: '',
                tipo: 'Implementación',
                hoja: '',
                comentarios: '',
                requiereEvidencia: true
            });
            setError(null);
            onClose();
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]"
            onClick={(e) => {
                if (e.target === e.currentTarget) {
                    onClose();
                }
            }}
        >
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Crear Nueva Evidencia de Seguridad
                        </h3>
                        <button
                            onClick={handleClose}
                            disabled={loading}
                            className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 disabled:opacity-50"
                        >
                            <X className="h-6 w-6" />
                        </button>
                    </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    {error && (
                        <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex items-center space-x-2">
                            <AlertCircle className="h-5 w-5" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Columna Izquierda */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Código de Control <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="codigoControl"
                                    value={formData.codigoControl}
                                    onChange={handleInputChange}
                                    required
                                    maxLength="200"
                                    placeholder="Ej: 3.1.5"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Descripción del Control <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    name="descripcionControl"
                                    value={formData.descripcionControl}
                                    onChange={handleInputChange}
                                    required
                                    maxLength="500"
                                    rows="3"
                                    placeholder="Descripción detallada del control de seguridad"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Prioridad <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="prioridad"
                                    value={formData.prioridad}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="Baja">Baja</option>
                                    <option value="Media">Media</option>
                                    <option value="Alta">Alta</option>
                                    <option value="Crítica">Crítica</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Intervalo de Revisión <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="intervaloRevision"
                                    value={formData.intervaloRevision}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="Mensual">Mensual</option>
                                    <option value="Trimestral">Trimestral</option>
                                    <option value="Semestral">Semestral</option>
                                    <option value="Anual">Anual</option>
                                    <option value="Bianual">Bianual</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Lista de Verificación <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="listaVerificacion"
                                    value={formData.listaVerificacion}
                                    onChange={handleInputChange}
                                    required
                                    maxLength="200"
                                    placeholder="Ej: 1. Verificar configuración de MFA"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Columna Derecha */}
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Descripción de Implementación <span className="text-red-500">*</span>
                                </label>
                                <textarea
                                    name="descripcionImplementacion"
                                    value={formData.descripcionImplementacion}
                                    onChange={handleInputChange}
                                    required
                                    maxLength="500"
                                    rows="3"
                                    placeholder="Descripción de cómo se implementa este control"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Tipo <span className="text-red-500">*</span>
                                </label>
                                <select
                                    name="tipo"
                                    value={formData.tipo}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                >
                                    <option value="Implementación">Implementación</option>
                                    <option value="Revisión Periódica">Revisión Periódica</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Hoja <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="hoja"
                                    value={formData.hoja}
                                    onChange={handleInputChange}
                                    required
                                    maxLength="200"
                                    placeholder="Ej: Hoja 3.1 - Controles de Acceso"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Comentarios
                                </label>
                                <textarea
                                    name="comentarios"
                                    value={formData.comentarios}
                                    onChange={handleInputChange}
                                    maxLength="1000"
                                    rows="3"
                                    placeholder="Comentarios adicionales (opcional)"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                />
                            </div>

                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    name="requiereEvidencia"
                                    checked={formData.requiereEvidencia}
                                    onChange={handleInputChange}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                    Requiere evidencia documental
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Botones */}
                    <div className="mt-8 flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={handleClose}
                            disabled={loading}
                            className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                            <Save className="h-4 w-4" />
                            <span>{loading ? 'Creando...' : 'Crear Evidencia'}</span>
                        </button>
                    </div>
                </form>
            </div>
        </div>,
        document.body
    );
}


